/**
 * useChildPreviousLogic.js
 * React hook for Child Previous Vaccination scenario
 *
 * Phase 4: Encapsulates ChildPreviousRules inside a stable hook interface
 * that is consistent with the DoseConfig contract from Phases 1-3.
 *
 * DIFFERENCES FROM useChildCurrentLogic:
 * - Accepts `selectedDoses` as a parameter (needed for cross-dose frequency
 *   constraints and sequential dependency checks within the same form session)
 * - Returns `checkboxEnabled` and `showDatePicker` instead of `showRadio`/`enabled`
 *   because this scenario uses checkbox + date picker (not radio buttons)
 * - Includes `getDateConstraints()` helper for direct DatePicker integration
 *
 * MEMOIZATION STRATEGY:
 * getDoseConfig is memoized on [vaccine, childDob, administeredDoses,
 * decryptFunction, selectedDoses]. The selectedDoses dependency is the key
 * addition over child-current. When the user checks a dose and enters a date,
 * selectedDoses changes, which invalidates the memo and recomputes the
 * frequency-adjusted minDate for subsequent doses. This is the desired
 * behaviour – it is NOT a performance concern because the recalculation is
 * cheap (pure dayjs math) and only fires on actual user input.
 */

import { useMemo, useCallback } from 'react';
import dayjs from 'dayjs';
import {
  getChildPreviousDoseStatus,
  calculateChildAgeInDays,
  getDatePickerConstraints
} from './ChildPreviousRules';

// ============================================================
// Base hook
// ============================================================

/**
 * Base hook for Child Previous vaccination logic.
 *
 * @param {Object}      vaccine           - Vaccine object { id, name, availableDoses, material }
 * @param {string}      childDob          - Child's date of birth string
 * @param {Array}       administeredDoses - Vaccination history from the profile API
 * @param {Function}    decryptFunction   - Stable decrypt fn (wrap in useCallback at call site)
 * @param {Array|null}  selectedDoses     - Current form dose selections (for cross-dose logic)
 *                                          Shape: [{ selected: bool, administeredDate: string }]
 * @returns {{
 *   getDoseConfig  : (doseIndex: number) => DoseConfig,
 *   getDateConstraints : (doseIndex: number) => DateConstraints,
 *   scenario       : string,
 *   isReady        : boolean,
 *   childAgeInDays : number
 * }}
 */
export const useChildPreviousLogic = (
  vaccine,
  childDob,
  administeredDoses,
  decryptFunction,
  selectedDoses = null,
  previousVaccines = [],
  identityContext = null
) => {
  // Pre-calculate age so it is consistent across all dose configs in one render cycle.
  // This also exposes the value to callers who want to display the age.: X months" without
  // calling calculateChildAgeInDays themselves.
  const childAgeInDays = useMemo(
    () => calculateChildAgeInDays(childDob),
    [childDob]
  );

  /**
   * Base dose config without selectedDoses dependency for better performance.
   * This is memoized independently from selectedDoses changes.
   */
  const getBaseDoseConfig = useMemo(() => {
    return (doseIndex) => {
      try {
        // Call with null selectedDoses first to get base status
        const rawStatus = getChildPreviousDoseStatus(
          vaccine,
          doseIndex,
          childDob,
          administeredDoses,
          decryptFunction,
          null, // No selectedDoses for base config
          previousVaccines,
          identityContext
        );

        return {
          // Checkbox interaction flags
          checkboxEnabled: rawStatus.checkboxEnabled ?? false,
          showCheckbox: rawStatus.showCheckbox ?? true,

          // Date picker flags + constraints
          showDatePicker: rawStatus.showDatePicker ?? false,
          minDate: rawStatus.minDate ?? null,
          maxDate: rawStatus.maxDate ?? null,

          // Status string – drives conditional rendering
          status: rawStatus.status ?? 'Unknown',

          // Date shown when status === 'administered'
          date: rawStatus.date ?? null,

          // Human-readable explanation for disabled / no_window states
          reason: rawStatus.reason ?? null,

          // Scenario metadata – useful for debugging and for shared
          // UI components that handle multiple scenarios
          scenario: 'child-previous',
          vaccineId: vaccine?.id ?? null,
          vaccineName: vaccine?.name ?? null,
          doseIndex,

          // Child age at time of render (useful for display + debugging)
          ageInDays: rawStatus.ageInDays ?? childAgeInDays
        };
      } catch (error) {
        // Return a safe disabled state rather than propagating the error
        // into the Formik render tree. The user sees a disabled checkbox.
        console.error(
          `useChildPreviousLogic.getBaseDoseConfig error for vaccine "${vaccine?.name}" dose ${doseIndex}:`,
          error
        );

        return {
          checkboxEnabled: false,
          showCheckbox: true,
          showDatePicker: false,
          minDate: null,
          maxDate: null,
          status: 'Error',
          date: null,
          reason: 'Could not determine eligibility – please refresh',
          scenario: 'child-previous',
          vaccineId: vaccine?.id ?? null,
          vaccineName: vaccine?.name ?? null,
          doseIndex,
          ageInDays: childAgeInDays
        };
      }
    };
  }, [vaccine, childDob, administeredDoses, decryptFunction, childAgeInDays, previousVaccines, identityContext]);

  /**
   * getDoseConfig: primary output of this hook with selectedDoses optimization.
   *
   * PERFORMANCE OPTIMIZATION: Only recalculates the specific dose when needed,
   * not all doses when selectedDoses changes.
   */
  const getDoseConfig = useCallback((doseIndex) => {
    const baseConfig = getBaseDoseConfig(doseIndex);

    // If no selectedDoses or dose already administered, return base config
    if (!selectedDoses || baseConfig.status === 'administered') {
      return baseConfig;
    }

    try {
      // Only recalculate with selectedDoses if base config passed initial checks
      if (baseConfig.status === 'enabled' || baseConfig.status === 'disabled') {
        const enhancedStatus = getChildPreviousDoseStatus(
          vaccine,
          doseIndex,
          childDob,
          administeredDoses,
          decryptFunction,
          selectedDoses,
          previousVaccines,
          identityContext
        );

        // Update only the parts that might change with selectedDoses
        return {
          ...baseConfig,
          checkboxEnabled: enhancedStatus.checkboxEnabled ?? baseConfig.checkboxEnabled,
          showDatePicker: enhancedStatus.showDatePicker ?? baseConfig.showDatePicker,
          minDate: enhancedStatus.minDate ?? baseConfig.minDate,
          maxDate: enhancedStatus.maxDate ?? baseConfig.maxDate,
          status: enhancedStatus.status ?? baseConfig.status,
          reason: enhancedStatus.reason ?? baseConfig.reason
        };
      }
    } catch (error) {
      console.error(
        `useChildPreviousLogic.getDoseConfig enhancement error for vaccine "${vaccine?.name}" dose ${doseIndex}:`,
        error
      );
    }

    return baseConfig;
  }, [getBaseDoseConfig, vaccine, childDob, administeredDoses, decryptFunction, selectedDoses, previousVaccines, identityContext]);

  /**
   * getDateConstraints: convenience helper for DatePicker integration.
   *
   * Returns an object that can be spread directly into MUI DatePicker props:
   *   const { minDate, maxDate, shouldDisableDate } = getDateConstraints(i);
   *   <DatePicker minDate={minDate} maxDate={maxDate} shouldDisableDate={shouldDisableDate} />
   *
   * This is a useCallback wrapping getDoseConfig so that the returned function
   * reference only changes when getDoseConfig itself changes, preventing
   * unnecessary DatePicker re-renders.
   */
  const getDateConstraints = useCallback(
    (doseIndex) => {
      const config = getDoseConfig(doseIndex);
      return getDatePickerConstraints(config);
    },
    [getDoseConfig]
  );

  return {
    getDoseConfig,
    getDateConstraints,
    scenario: 'child-previous',
    // isReady lets callers defer rendering until all required data is loaded.
    // selectedDoses is intentionally NOT part of the isReady check because
    // the form starts with no selections and that is a valid initial state.
    isReady: !!(vaccine && childDob && decryptFunction),
    // Expose pre-calculated age as a convenience
    childAgeInDays
  };
};

// ============================================================
// Enhanced hook
// ============================================================

/**
 * Enhanced hook for Child Previous vaccination.
 *
 * Extends the base hook with:
 *   - allDoseConfigs: all configs computed in one memoized pass
 *   - Summary booleans (hasEnabledDoses, hasAdministeredDoses, etc.)
 *   - validateAllSelectedDoses: cross-dose validation for form submission
 *   - shouldShowDatePicker: UI helper that combines config + form state
 *
 * Use this variant when the parent component needs summary state (e.g., to
 * show an empty-state message) or needs to run pre-submit validation.
 *
 * @param {Object}      vaccine           - Vaccine object
 * @param {string}      childDob          - Child's DOB
 * @param {Array}       administeredDoses - Vaccination history
 * @param {Function}    decryptFunction   - Stable decrypt function
 * @param {Array|null}  selectedDoses     - Current form dose selections
 * @param {Array}       previousVaccines  - List of previous vaccines
 * @param {Object}      [options]         - Optional config
 * @param {boolean}     [options.debug]   - Include debugInfo in the return value
 * @returns {Object} - Enhanced hook result
 */
export const useChildPreviousLogicEnhanced = (
  vaccine,
  childDob,
  administeredDoses,
  decryptFunction,
  selectedDoses = null,
  previousVaccines = [],
  identityContext = null,
  options = {}
) => {
  const baseLogic = useChildPreviousLogic(
    vaccine,
    childDob,
    administeredDoses,
    decryptFunction,
    selectedDoses,
    previousVaccines,
    identityContext
  );

  // Destructure stable refs BEFORE useMemo so the dependency array only holds
  // referentially-stable values. The baseLogic object itself is re-created on
  // every render (it's a plain object literal in the return statement of
  // useChildPreviousLogic), so listing it as a dep would defeat memoization.
  const { getDoseConfig, isReady } = baseLogic;

  // Compute all dose configs with performance optimization.
  // Base configs are memoized separately from selectedDoses-dependent configs.
  const allDoseConfigs = useMemo(() => {
    if (!isReady || !vaccine?.availableDoses?.length) {
      return [];
    }
    // Use the optimized getDoseConfig that only recalculates what's necessary
    return vaccine.availableDoses.map((_, index) => getDoseConfig(index));
  }, [isReady, vaccine?.availableDoses, getDoseConfig]);

  /**
   * shouldShowDatePicker: combines DoseConfig with live form state.
   *
   * The DoseConfig's `showDatePicker` says "CAN a picker be shown for this dose?"
   * This function additionally checks whether the user has actually ticked the
   * checkbox (`dose.selected === true`) before returning true.
   *
   * WHY THIS BELONGS HERE AND NOT IN THE RULES FILE:
   * It depends on form state (selectedDoses), which is runtime React state.
   * Pure rules functions must remain stateless so they can be unit tested
   * without a React environment.
   */
  const shouldShowDatePicker = useCallback(
    (doseIndex) => {
      const config = allDoseConfigs[doseIndex];
      if (!config?.showDatePicker) return false;

      // Check if the user has selected this dose in the current form session
      const isSelected = selectedDoses?.[doseIndex]?.selected === true;
      return isSelected;
    },
    [allDoseConfigs, selectedDoses]
  );

  /**
   * validateAllSelectedDoses: run all business-rule validations across every
   * dose the user has checked. Called before form submission.
   *
   * Returns an errors array so the caller can display per-dose error messages.
   * An empty array means the form is valid.
   */
  const validateAllSelectedDoses = useCallback(() => {
    const errors = [];

    if (!selectedDoses) return { isValid: true, errors };

    selectedDoses.forEach((dose, doseIndex) => {
      if (!dose.selected || !dose.administeredDate) return;

      const config = allDoseConfigs[doseIndex];
      if (!config) return;

      // Cross-check: the user-entered date must fall within the computed window
      if (config.minDate && config.maxDate) {
        const enteredDate = dayjs(dose.administeredDate);

        if (enteredDate.isBefore(dayjs(config.minDate), 'day')) {
          errors.push({
            doseIndex,
            vaccineName: vaccine?.name,
            message: `Date is before the earliest valid date (${dayjs(config.minDate).format('DD/MM/YYYY')})`
          });
        } else if (enteredDate.isAfter(dayjs(config.maxDate), 'day')) {
          errors.push({
            doseIndex,
            vaccineName: vaccine?.name,
            message: `Date is after the latest valid date (${dayjs(config.maxDate).format('DD/MM/YYYY')})`
          });
        }
      }
    });

    return {
      isValid: errors.length === 0,
      errors
    };
  }, [allDoseConfigs, selectedDoses, vaccine?.name]);

  // Stable per-index helpers
  const isDoseCheckboxEnabled = useCallback(
    (doseIndex) => allDoseConfigs[doseIndex]?.checkboxEnabled ?? false,
    [allDoseConfigs]
  );

  const getDoseStatus = useCallback(
    (doseIndex) => allDoseConfigs[doseIndex]?.status ?? 'Unknown',
    [allDoseConfigs]
  );

  return {
    // Spread base logic so callers can use getDoseConfig / getDateConstraints directly
    ...baseLogic,

    // Batch configs
    allDoseConfigs,

    // UI helpers
    shouldShowDatePicker,
    isDoseCheckboxEnabled,
    getDoseStatus,

    // Pre-submit validation
    validateAllSelectedDoses,

    // Summary booleans for empty-state and header rendering
    hasEnabledDoses: allDoseConfigs.some((c) => c.status === 'enabled'),
    hasAdministeredDoses: allDoseConfigs.some((c) => c.status === 'administered'),
    hasDisabledDoses: allDoseConfigs.some((c) => c.status === 'disabled'),
    hasNoWindowDoses: allDoseConfigs.some((c) => c.status === 'no_window'),

    // Optional debug info – only populated when options.debug is true
    debugInfo: options.debug
      ? {
          vaccine: vaccine?.name,
          childDob,
          childAgeInDays: baseLogic.childAgeInDays,
          administeredCount: administeredDoses?.length ?? 0,
          selectedCount: selectedDoses?.filter((d) => d?.selected)?.length ?? 0,
          doseConfigs: allDoseConfigs
        }
      : null
  };
};
